import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

import { NextPage, GetServerSideProps } from "next";
import {
  Bundle,
  Client,
  BundleExtension,
  Product,
  Claiming,
  Invoice,
} from "@prisma/client";
import { isAfter } from "date-fns";

import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";
import { createTheme, Theme, ThemeProvider } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";

import { themeOptions } from "theme";

import { ItemHistory } from "sections/itemsPage/SectionHistory";
import ProductView from "sections/itemsPage/ProductView";

import Layout from "components/Layout";
import SocialGroup from "components/SocialGroup";

import {
  getByContractAddressAndTokenId as getProductByContractAddressAndTokenId,
  getHashByContractAddressAndTokenId,
} from "db/repository/ProductRepository";
import { getByContractAddressAndTokenId as getAllClaimingsByContractAddressAndTokenId } from "db/repository/ClaimingRepository";

import { PAGE_TYPES } from "enums/pageTypes";
import { THEME_MODES } from "enums/themeModes";
import { CHAIN_IDS } from "enums/chainIds";
import { PURPOSES_TYPES } from "enums/purposesTypes";

import { Metadata } from "interfaces/Metadata.interface";

import { ETHER_ADDRESS_O } from "config/addresses";

import { mapPurpose } from "utils/redeem";
import { captureException } from "utils/errors";
import { getSubdomain } from "utils/store";
import { getCombinedMetadata } from "utils/metadata";

import ProviderFactory from "services/blockchain/ProviderFactory";
import getNftMetadata from "services/metadata/nft";
import CollectionService from "services/collection/CollectionService";

import { withSessionSsr } from "lib/session";

interface NfcItemPageProps {
  productData: Product & {
    bundle: Bundle & {
      metadata: Metadata;
      bundleExtensions: BundleExtension[];
      client: Client & {
        invoice: Pick<Invoice, "email">;
      };
    };
    metadata: Metadata;
    codeRequired: boolean;
  };
  claimings:
    | Pick<
        Claiming,
        | "updatedAt"
        | "purpose"
        | "transactionHash"
        | "chainId"
        | "nickname"
        | "metamaskAddress"
        | "size"
        | "dclContractAddress"
        | "dclTokenId"
        | "productionStatus"
      >[]
    | null;
  onChainMetadata: Metadata | null;
  owner: string | null;
  itemHistory: ItemHistory[];
  isTagValid: boolean;
  mintTransactionHash: string;
  nickname: string;
  itemChainId: CHAIN_IDS;
  isEmbedded: boolean;
}

const NfcItemPage: NextPage<NfcItemPageProps> = ({
  productData,
  onChainMetadata,
  owner,
  itemHistory,
  claimings,
  mintTransactionHash,
  nickname,
  itemChainId,
  isEmbedded,
}) => {
  const { metadata: offChainBundleMetadata } = productData?.bundle || {};
  const { metadata: offChainProductMetadata } = productData || {};
  const { template, templateConfig } = productData?.bundle?.client || {};

  const theme: Theme = createTheme(
    deepmerge(themeOptions(THEME_MODES.DARK), templateConfig || {}),
  );

  const metadata: Metadata = getCombinedMetadata(
    onChainMetadata,
    offChainProductMetadata,
    offChainBundleMetadata,
  );

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY as string}
    >
      <Layout
        isHeaderAlwaysActive
        logo={productData.bundle.client.logo || undefined}
        customFooterSocials={<SocialGroup items={productData.bundle.client} />}
        isEmbedded={isEmbedded}
      >
        <ThemeProvider theme={theme}>
          <GlobalStyles
            styles={{
              body: {
                backgroundColor: theme.palette.background.default,
              },
            }}
          />
          <Box sx={{ color: "text.primary" }}>
            {/* Here you can add custom enterprise layouts */}
            {!template || template === "DEFAULT" ? (
              <ProductView
                productData={productData}
                metadata={metadata}
                owner={owner}
                itemHistory={itemHistory}
                claimings={claimings}
                mintTransactionHash={mintTransactionHash}
                itemChainId={itemChainId}
                pageType={PAGE_TYPES.VOUCHER}
                nickname={nickname}
              />
            ) : null}
          </Box>
        </ThemeProvider>
      </Layout>
    </GoogleReCaptchaProvider>
  );
};

export const getServerSideProps: GetServerSideProps = withSessionSsr(
  async (ctx) => {
    const isEmbedded =
      !!getSubdomain(ctx.req.headers.host as string) || !!ctx.query?.embedded;

    const { pid } = ctx.query;
    const [contractAddress, token] = pid as string[];
    const tokenId = parseInt(token);
    const user = ctx.req.session.user;

    try {
      if (!Number.isInteger(tokenId)) {
        throw new Error(`Token ID ${token} is not valid!`);
      }

      const productData = await getProductByContractAddressAndTokenId(
        contractAddress,
        tokenId,
      );

      if (!productData) {
        throw new Error(
          `Product ${contractAddress}/${tokenId} does not exist!`,
        );
      }

      const [productHash, claimings, chainId] = await Promise.all([
        productData.bundle?.client?.nfcContractAddress &&
          getHashByContractAddressAndTokenId(
            productData.bundle.client.nfcContractAddress,
            tokenId,
          ),
        getAllClaimingsByContractAddressAndTokenId(
          productData.contractAddress,
          productData.tokenId,
        ),
        CollectionService.getChainIdByAddress(
          productData.contractAddress ||
            productData.bundle?.client.mintContractAddress ||
            "",
        ),
      ]);
      productData.hash = productHash ?? null;

      const provider = ProviderFactory.create(chainId);

      const {
        metadata: onChainMetadata,
        owner: productOwner,
        user: productUser,
        transactionHash: mintTransactionHash,
        transactionDate: mintTransactionDate,
      } = await getNftMetadata(
        productData.contractAddress,
        productData.tokenId,
        user?.metamaskAddress,
        provider,
      );

      if (
        contractAddress === "0xa5446d4c8e4c2c1a36f667db72553a1c105191b4" &&
        onChainMetadata?.name?.includes("Box")
      ) {
        throw new Error(`This JRNY Box has not been minted yet!`);
      }

      const owner = productOwner === ETHER_ADDRESS_O ? null : productOwner;

      // ITEM HISTORY
      const itemHistory: ItemHistory[] = claimings.map((claiming) => ({
        ...claiming,
        chainId: (claiming.chainId as CHAIN_IDS) || chainId,
        updatedAt: claiming.updatedAt?.getTime(),
        name: `Redeem ${mapPurpose(claiming.purpose as PURPOSES_TYPES)}`,
      }));

      const nickname =
        productUser?.nickname || productData.user?.nickname || "";

      mintTransactionHash &&
        itemHistory.push({
          updatedAt: mintTransactionDate
            ? new Date(mintTransactionDate).getTime()
            : new Date().getTime(),
          transactionHash: mintTransactionHash,
          metamaskAddress: owner || "",
          name: "Mint voucher NFT",
          chainId,
          nickname,
        });

      const sortedItemHistory = itemHistory.sort((a, b) =>
        isAfter(new Date(a.updatedAt ?? null), new Date(b.updatedAt ?? null))
          ? -1
          : 1,
      );

      return {
        props: {
          productData: JSON.parse(JSON.stringify(productData)),
          claimings: JSON.parse(JSON.stringify(claimings)),
          owner: owner || "",
          onChainMetadata,
          itemHistory: sortedItemHistory,
          mintTransactionHash,
          itemChainId: chainId,
          nickname: productUser?.nickname ?? "",
          isEmbedded,
        },
      };
    } catch (e) {
      captureException(
        `[ITEM PAGE] - ${contractAddress}/${tokenId} - ${JSON.stringify(e)}`,
      );
      return {
        redirect: {
          destination: "/404",
          permanent: false,
        },
      };
    }
  },
);

export default NfcItemPage;
